import { Chart, Plugin } from 'chart.js';
import { SitePerformanceChartUtils } from '../utils/SitePerformanceChartUtils';
import { getSitePerformanceChartJsHoveredSiteIndex } from './sitePerformanceChartJsBarHoverPlugin';
import {
  BAR_LABEL_GAP,
  FULL_WIDTH_BAR_THRESHOLD,
  SITE_PERFORMANCE_CHART_JS_Y_AXIS_WIDTH,
  SITE_PERFORMANCE_CHART_UNHOVERED_BAR_VALUE_LABEL_COLOR,
} from 'app/modules/site-management/widgets/fleet-dashboard/constants/SitePerformanceChart.constants';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';

const getBarValueLabelFillStyle = (isHoveredSite: boolean): string => {
  if (!isHoveredSite) {
    return SITE_PERFORMANCE_CHART_UNHOVERED_BAR_VALUE_LABEL_COLOR;
  }

  return kccThemeConstants.COLORS.TEXT;
};

export const sitePerformanceChartJsYAxisWidthPlugin: Plugin = {
  id: 'sitePerformanceChartJsYAxisWidth',
  beforeLayout(chart: Chart): void {
    const yScale = chart.scales.y;

    if (yScale) {
      yScale.width = SITE_PERFORMANCE_CHART_JS_Y_AXIS_WIDTH;
    }
  },
};

export const sitePerformanceChartJsBarValueLabelPlugin: Plugin = {
  id: 'sitePerformanceChartJsBarValueLabels',
  afterDatasetsDraw(chart: Chart): void {
    const { ctx, chartArea } = chart;

    if (!chartArea || chartArea.width <= 0) {
      return;
    }

    const hoveredSiteIndex = getSitePerformanceChartJsHoveredSiteIndex(chart);

    ctx.save();
    ctx.textBaseline = 'middle';
    const { EXTRA_BOLD } = kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT;
    const { XXS } = kccThemeConstants.TYPOGRAPHY.FONT_SIZE;
    const { FONT_FAMILY } = kccThemeConstants.TYPOGRAPHY;
    ctx.font = `${EXTRA_BOLD} ${XXS}px ${FONT_FAMILY}`;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      if (meta.hidden) {
        return;
      }

      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];

        if (typeof value !== 'number' || !value) {
          return;
        }

        const isHoveredSite = hoveredSiteIndex === null || index === hoveredSiteIndex;
        const label = SitePerformanceChartUtils.formatBarValue(value);
        const labelWidth = ctx.measureText(label).width;
        const barEndX = bar.x;
        const barY = bar.y;
        const plotRight = chartArea.right;
        const spaceOutside = plotRight - barEndX;
        const isFullWidthBar = barEndX >= plotRight - FULL_WIDTH_BAR_THRESHOLD;
        const labelFitsOutside = spaceOutside >= BAR_LABEL_GAP + labelWidth;

        if (labelFitsOutside) {
          const labelX = barEndX + BAR_LABEL_GAP;

          ctx.textAlign = 'left';
          ctx.fillStyle = getBarValueLabelFillStyle(isHoveredSite);
          ctx.fillText(label, labelX, barY);
        } else if (isFullWidthBar) {
          const labelX = barEndX - BAR_LABEL_GAP;

          ctx.textAlign = 'right';
          ctx.fillStyle = getBarValueLabelFillStyle(isHoveredSite);
          ctx.fillText(label, labelX, barY);
        } else {
          const labelX = barEndX + BAR_LABEL_GAP;

          ctx.textAlign = 'left';
          ctx.fillStyle = getBarValueLabelFillStyle(isHoveredSite);
          ctx.fillText(label, labelX, barY);
        }
      });
    });

    ctx.restore();
  },
};
